import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import NotificationsBell from "../components/NotificationsBell";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowLeft, LogOut, Camera, ChevronDown, ChevronRight, Loader2, CheckCircle2, Check, RotateCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import TaskContractorUpdates, { TaskUpdateComposer } from "../components/TaskContractorUpdates";
import { compressImage } from "../lib/imageCompression";
import DefectsPanel from "../components/DefectsPanel";

export default function ContractorProject() {
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["contractor", "common"]);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [vos, setVos] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [p, t, v] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/tasks`),
      api.get(`/projects/${projectId}/vos`),
    ]);
    setProject(p.data.project);
    setMilestones(p.data.milestones || []);
    setTasks(t.data.tasks || []);
    setVos(v.data.vos || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const doLogout = async () => { await logout(); navigate("/"); };

  // Backend already scopes /projects/:id/tasks to this contractor's owner_contractor_id
  // and strips the pricing columns. Just filter out completed tasks.
  const myTasks = tasks.filter((t) => t.status !== "done");
  const [expandedTask, setExpandedTask] = useState(null);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const toggle = (id) => setExpandedTask((cur) => (cur === id ? null : id));

  // After any task action (posting an update, marking complete), we need to:
  //   1. Refetch project tasks so pills like "Awaiting review" appear on the tile.
  //   2. Force the timeline component inside the expanded card to re-fetch.
  const bumpAfterAction = useCallback(async () => {
    setTimelineRefreshKey((k) => k + 1);
    await reload();
  }, [reload]);

  // Group tasks by milestone so the contractor sees which milestone each task belongs to.
  const msNameById = new Map(milestones.map((m) => [m.milestone_id, m.name]));
  const grouped = (() => {
    const map = new Map();
    for (const t of myTasks) {
      const key = t.milestone_id || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    // Preserve milestone order defined by the firm
    const orderedIds = [
      ...milestones.map((m) => m.milestone_id).filter((id) => map.has(id)),
      ...(map.has("__none__") ? ["__none__"] : []),
    ];
    return orderedIds.map((id) => ({
      milestone_id: id,
      milestone_name: id === "__none__" ? t("contractor:project.unassigned") : (msNameById.get(id) || t("contractor:project.milestoneFallback")),
      tasks: map.get(id) || [],
    }));
  })();

  const markTaskDone = async (task_id) => {
    try { await api.patch(`/tasks/${task_id}`, { status: "done" }); toast.success(t("contractor:project.toasts.nice")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#7A7571]">{t("common:actions.loading")}</div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center text-[#7A7571]">{t("contractor:project.notFound")}</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-16">
      <header className="border-b border-[#EBE5D9] sticky top-0 z-30 bg-[#FDFBF7]/95 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/contractor" className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29]">
            <ArrowLeft size={16} /> {t("contractor:project.backToProjects")}
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="header" />
            <NotificationsBell />
            <button onClick={doLogout} data-testid="logout-btn" className="btn-ghost !p-2"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{t("contractor:project.contractorView")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2C2A29] mt-1">{project.name}</h1>
          <div className="text-[#7A7571] mt-1">{project.address}</div>
        </div>

        <div className="card-soft">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">{t("contractor:project.yourTasks")}</div>
          {grouped.length === 0 && <p className="text-[#7A7571]">{t("contractor:project.nothingOnPlate")}</p>}
          <div className="space-y-6">
            {grouped.map((g) => (
              <div key={g.milestone_id} data-testid={`ctr-milestone-group-${g.milestone_id}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-display text-lg font-bold text-[#2C2A29]">{g.milestone_name}</h3>
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7571]">
                    {t("contractor:project.taskCount", { count: g.tasks.length })}
                  </span>
                </div>
                <div className="space-y-3">
                  {g.tasks.map((tk) => {
                    const open = expandedTask === tk.task_id;
                    return (
                <div
                  key={tk.task_id}
                  className="rounded-2xl bg-[#F7F4EC] overflow-hidden"
                  data-testid={`ctr-task-${tk.task_id}`}
                >
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      onClick={() => toggle(tk.task_id)}
                      className="w-full p-4 text-left flex-1 min-w-0"
                    >
                      {/* Row 1: status pill (small) */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#B85536] bg-[#FFE4D6] px-2 py-0.5 rounded-full">
                            {t("contractor:project.withContractor")}
                          </div>
                          {tk.rework_count > 0 && !tk.contractor_completed_at && (
                            <div
                              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#9A3412] bg-[#FFEDD5] border border-[#FDBA74] px-2 py-0.5 rounded-full animate-pulse"
                              data-testid={`ctr-task-rework-pill-${tk.task_id}`}
                            >
                              <RotateCcw size={10} /> {t("contractor:project.reworkRequested")}
                            </div>
                          )}
                          {tk.contractor_completed_at && (
                            <div
                              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#166534] bg-[#DCFCE7] border border-[#86EFAC] px-2 py-0.5 rounded-full"
                              data-testid={`ctr-task-review-pill-${tk.task_id}`}
                            >
                              <Check size={10} /> {t("contractor:project.awaitingReview")}
                            </div>
                          )}
                        </div>
                        {open
                          ? <ChevronDown size={16} className="text-[#7A7571] flex-shrink-0" />
                          : <ChevronRight size={16} className="text-[#7A7571] flex-shrink-0" />}
                      </div>
                      {/* Row 2: title + item code */}
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-semibold text-[#2C2A29] break-words">{tk.title}</span>
                        {tk.item_code && (
                          <span className="text-xs text-[#7A7571]">#{tk.item_code}</span>
                        )}
                      </div>
                      {/* Row 3: description */}
                      {tk.description && (
                        <div className="text-sm text-[#7A7571] mt-1 leading-relaxed break-words whitespace-pre-wrap">
                          {tk.description}
                        </div>
                      )}
                      {/* Row 3b: VO line-item breakdown (no cost) */}
                      {tk.vo_line_items && tk.vo_line_items.length > 0 && (
                        <ul className="mt-2 space-y-1 rounded-xl bg-[#FBF7EF]/70 border border-[#EBE5D9] p-2" data-testid={`ctr-task-vo-lines-${tk.task_id}`}>
                          {tk.vo_line_items.map((li, idx) => (
                            <li key={idx} className="flex items-baseline gap-2 text-sm">
                              <span className="font-mono text-[11px] font-semibold text-[#7A7571] w-8 flex-shrink-0">{li.item_code || `#${idx + 1}`}</span>
                              <span className="flex-1 text-[#2C2A29] break-words">{li.description}</span>
                              {li.qty != null && (
                                <span className="text-[11px] text-[#7A7571] whitespace-nowrap">
                                  {t("contractor:project.qtyLabel")} <b className="text-[#2C2A29]">{li.qty}{li.unit ? ` ${li.unit}` : ""}</b>
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* Row 4: qty (only for non-VO tasks with an aggregate qty+unit) */}
                      {tk.qty != null && tk.unit && !(tk.vo_line_items && tk.vo_line_items.length) && (
                        <div className="text-xs text-[#7A7571] mt-2">
                          {t("contractor:project.qtyFull")} <span className="font-semibold text-[#2C2A29]">{tk.qty} {tk.unit}</span>
                        </div>
                      )}
                    </button>
                    <QuickCameraButton
                      taskId={tk.task_id}
                      onUploaded={() => setExpandedTask(tk.task_id)}
                    />
                  </div>
                  {open && (
                    <div className="px-4 pb-4 bg-white/50 space-y-3">
                      <TaskUpdateComposer
                        taskId={tk.task_id}
                        placeholder={t("contractor:project.composerPlaceholder")}
                        onPosted={bumpAfterAction}
                      />
                      <MarkCompletedButton
                        task={tk}
                        onDone={bumpAfterAction}
                      />
                      <TaskContractorUpdates
                        taskId={tk.task_id}
                        autoLoadWhen={open}
                        hideComposer
                        refreshKey={timelineRefreshKey}
                      />
                    </div>
                  )}
                </div>
              );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-soft">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">{t("contractor:project.defectsAssigned")}</div>
          <DefectsPanel projectId={projectId} viewer="contractor" onExternalChange={reload} />
        </div>

        <div className="card-soft">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-4">{t("contractor:project.vosYouRaised")}</div>
          {vos.filter((v) => v.raised_by_id === user?.user_id).length === 0 && <p className="text-[#7A7571]">{t("contractor:project.noneYet")}</p>}
          {vos.filter((v) => v.raised_by_id === user?.user_id).map((v) => (
            <div key={v.vo_id} className="p-3 rounded-2xl bg-[#F7F4EC] mb-2">
              <div className="flex items-center justify-between"><div className="font-semibold text-[#2C2A29]">{v.title}</div></div>
              <div className="text-sm text-[#7A7571]">${Number(v.cost_delta).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function MarkCompletedButton({ task, onDone }) {
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation("contractor");
  const done = !!task.contractor_completed_at;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.task_id}/contractor-complete`);
      toast.success(t("project.toasts.sentToIdFirm"));
      onDone && (await onDone());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const when = new Date(task.contractor_completed_at).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
    return (
      <div className="mt-3 mb-3 rounded-2xl bg-[#DCFCE7] border border-[#86EFAC] p-3 text-sm text-[#166534]" data-testid={`ctr-completed-${task.task_id}`}>
        <div className="flex items-center gap-2 font-semibold">
          <Check size={16} /> {t("project.sentForReview")}
        </div>
        <div className="text-xs mt-0.5">{t("project.sentForReviewDetail", { when })}</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={submit}
      disabled={busy}
      data-testid={`ctr-mark-done-${task.task_id}`}
      className="w-full my-3 py-3 rounded-2xl bg-[#166534] text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#14532D] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
      {busy ? t("project.sending") : t("project.markCompleted")}
    </button>
  );
}

function QuickCameraButton({ taskId, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation("contractor");
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("files", compressed);
      await api.post(`/tasks/${taskId}/contractor-updates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("project.toasts.photoUploaded"));
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
        disabled={busy}
        data-testid={`ctr-task-camera-${taskId}`}
        title={t("project.takePhotoTitle")}
        className="px-3 flex items-center justify-center border-l border-[#EBE5D9] hover:bg-white transition-colors"
      >
        {busy ? <Loader2 size={18} className="animate-spin text-[#7A7571]" /> : <Camera size={20} className="text-[#B85536]" />}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
    </>
  );
}
